import type { LocalJourney, JournalCommand, JournalEvidence, RejectedJourneyEvidence, RejectedJourneyCommand } from './travelJournal.ts';
import type { JourneyConfig, JourneyPoint } from './travel.ts';
import type { CostRecord } from '../react/components/costs/costModel.ts';

export interface JourneyPending { commands: JournalCommand[]; points: JourneyPoint[]; evidence: JournalEvidence[] }
interface StartDraft { payload: Record<string, unknown>; cost?: CostRecord }
interface FinishDraft { owner: string; costId: string; sessionId: string; deviceId: string; payload: Record<string, unknown>; completedCost?: CostRecord }
interface ServerStatus { costId: string; sessionId: string | null; state: string; stopRequestedAt?: string | null }
interface RejectedCommandRecord { operationId: string; code: string; durable: boolean }
export interface JourneyControllerDependencies {
  owner(): string | null;
  canWrite(): boolean;
  online(): boolean;
  changed(): void;
  now(): string;
  deviceId(): string;
  operationId(): string;
  read<T>(key: string): Promise<T | null>;
  update<T>(key: string, update: (current: T | null) => T | null): Promise<T | null>;
  request<T>(owner: string, path: string, body?: unknown): Promise<T>;
  uploadPoints(path: string, token: string, body: unknown): Promise<{ sessionId: string; acknowledgedSequences: number[] }>;
  nativeAvailable(): boolean;
  capabilities(): Promise<{ version: number; platform: string; background: boolean }>;
  requestLocation(): Promise<boolean>;
  local(): Promise<LocalJourney | null>;
  prepare(session: LocalJourney): Promise<unknown>;
  command(action: JournalCommand['action'], fields?: Record<string, unknown>, session?: LocalJourney): Promise<unknown>;
  replaceCommand(session: LocalJourney, operationId: string, fields: Record<string, unknown>, durable: boolean): Promise<unknown>;
  pending(session?: LocalJourney): Promise<JourneyPending>;
  acknowledge(value: { operationIds?: string[]; throughSequence?: number; evidenceIds?: string[] }, session: LocalJourney): Promise<unknown>;
  clear(session: LocalJourney): Promise<unknown>;
}
const path = (id: string, suffix: string) => `/api/costs/travel/${encodeURIComponent(id)}/${suffix}`;
const sameJourney = (a: LocalJourney, b: Pick<LocalJourney, 'owner' | 'costId' | 'sessionId' | 'deviceId'>) =>
  ['owner', 'costId', 'sessionId', 'deviceId'].every(key => a[key as keyof typeof b] === b[key as keyof typeof b]);
const rejectedInput = (error: unknown) => [400, 404, 422].includes((error as { status?: number }).status || 0);

/** Recorder and transport adapters are injected so recovery invariants can be
 * tested without a provider, device, IndexedDB substitute or background process. */
export function createJourneyController(deps: JourneyControllerDependencies) {
  const syncing = new Map<string, Promise<void>>();
  const starts = new Map<string, Promise<CostRecord>>();
  const finishes = new Map<string, Promise<CostRecord>>();
  const owner = () => { const value = deps.owner(); if (!value) throw new Error('AUTH_REQUIRED'); return value; };
  const assertOwner = (identity: string) => { if (deps.owner() !== identity) throw new Error('TRAVEL_SESSION_CONFLICT'); };
  const local = async (identity: string, expected?: LocalJourney) => {
    assertOwner(identity); const value = await deps.local(); assertOwner(identity);
    if (value && (value.owner !== identity || expected && !sameJourney(value, expected))) throw new Error('TRAVEL_SESSION_CONFLICT');
    return value;
  };
  async function readJourneyDraft() { const identity = owner(); const result = await deps.read<StartDraft>(`${identity}:draft`); assertOwner(identity); return result; }
  function startJourney(payload: Record<string, unknown>, recordingMode: 'native' | 'manual', config: JourneyConfig) {
    const identity = owner();
    const running = starts.get(identity); if (running) return running;
    const task = start(identity, payload, recordingMode, config).finally(() => { if (starts.get(identity) === task) starts.delete(identity); });
    starts.set(identity, task); return task;
  }
  async function start(identity: string, payload: Record<string, unknown>, recordingMode: 'native' | 'manual', config: JourneyConfig) {
    let draft = await readJourneyDraft(); const prior = await local(identity);
    const finished = await deps.read<FinishDraft>(`${identity}:finish`); assertOwner(identity);
    if (!prior && finished) {
      if (!finished.completedCost || finished.owner !== identity) throw new Error('PREVIOUS_JOURNEY_UNRECONCILED');
      await deps.update<FinishDraft>(`${identity}:finish`, current => {
        assertOwner(identity); return current?.sessionId === finished.sessionId && current.completedCost ? null : current;
      });
    }
    if (prior && (!draft?.cost || !sameJourney(prior, { owner: identity, costId: draft.cost.id, sessionId: draft.cost.travel?.sessionId || '', deviceId: String(draft.payload.deviceId) }))) throw new Error('PREVIOUS_JOURNEY_UNRECONCILED');
    const mode = draft?.payload.recordingMode || recordingMode;
    const capabilities = await deps.capabilities(); assertOwner(identity);
    if (mode === 'native' && (!deps.nativeAvailable() || !capabilities.background || capabilities.version < config.minimumRecorderVersion)) throw new Error('TRAVEL_NATIVE_UPGRADE_REQUIRED');
    if (mode === 'native' && (!prior || prior.state === 'PREPARING') && !await deps.requestLocation()) throw new Error('TRAVEL_LOCATION_DENIED');
    assertOwner(identity);
    if (!draft) {
      const next: StartDraft = { payload: { ...payload, workflowVersion: 2, deviceId: deps.deviceId(), platform: capabilities.platform,
        recorderVersion: capabilities.version, recordingMode, noticeAcknowledged: true, noticeVersion: config.noticeVersion,
        reportingTimezone: config.reportingTimezone, idempotencyKey: deps.operationId() } };
      draft = await deps.update<StartDraft>(`${identity}:draft`, current => { assertOwner(identity); return current || next; });
    }
    assertOwner(identity); if (!draft) throw new Error('JOURNAL_UNAVAILABLE');
    let cost = draft.cost;
    if (!cost) {
      try { cost = (await deps.request<{ cost: CostRecord }>(identity, '/api/costs/travel/start', draft.payload)).cost; }
      catch (error) {
        if (rejectedInput(error)) await deps.update<StartDraft>(`${identity}:draft`, current => {
          assertOwner(identity); return current && current.payload.idempotencyKey === draft!.payload.idempotencyKey && !current.cost ? null : current;
        });
        throw error;
      }
    }
    assertOwner(identity); if (!cost?.travel?.sessionId) throw new Error('TRAVEL_SESSION_REQUIRED');
    const stored = await deps.update<StartDraft>(`${identity}:draft`, current => {
      assertOwner(identity);
      if (!current || current.payload.idempotencyKey !== draft!.payload.idempotencyKey) throw new Error('TRAVEL_SESSION_CONFLICT');
      return { ...current, cost };
    });
    assertOwner(identity); if (!stored) throw new Error('JOURNAL_UNAVAILABLE');
    await deps.prepare({ owner: identity, costId: cost.id, sessionId: cost.travel.sessionId, deviceId: String(stored.payload.deviceId),
      revision: cost.travel.revision || 0, eventSequence: cost.travel.revision || 0, state: 'PREPARING',
      recordingMode: stored.payload.recordingMode as 'native' | 'manual', recording: false, pointSequence: 0,
      commands: [], evidence: [], odometerUnit: String(stored.payload.odometerUnit || 'KM'),
      vehicleLabel: typeof stored.payload.vehicleLabel === 'string' ? stored.payload.vehicleLabel : undefined, startedAt: deps.now() });
    const prepared = await local(identity);
    if (!prepared || prepared.costId !== cost.id || prepared.sessionId !== cost.travel.sessionId) throw new Error('TRAVEL_SESSION_CONFLICT');
    if (prepared.state === 'PREPARING') await deps.command('activate', {}, prepared);
    assertOwner(identity);
    await deps.update<StartDraft>(`${identity}:draft`, current => {
      assertOwner(identity); return current?.payload.idempotencyKey === stored.payload.idempotencyKey ? null : current;
    });
    deps.changed(); void synchronizeJourney().catch(() => deps.changed()); return cost;
  }
  async function journeyAction(action: 'arrive' | 'depart' | 'end' | 'health' | 'correct', fields: Record<string, unknown> = {}) {
    const identity = owner();
    // Native stop is invoked immediately; the adapter stops hardware before disk
    // persistence, network activity or the final camera step.
    await deps.command(action, fields); assertOwner(identity); deps.changed();
    void synchronizeJourney().catch(() => deps.changed());
  }
  function synchronizeJourney(): Promise<void> {
    const identity = deps.owner(); if (!identity) return Promise.resolve();
    const running = syncing.get(identity); if (running) return running;
    const task = upload(identity).catch(async error => {
      if ([401, 403].includes((error as { status?: number }).status || 0) && deps.owner() === identity) {
        const session = await local(identity);
        if (session?.recording) { await deps.command('health', {}, session); deps.changed(); }
      }
      throw error;
    }).finally(() => { if (syncing.get(identity) === task) syncing.delete(identity); });
    syncing.set(identity, task); return task;
  }
  async function upload(identity: string) {
    let session = await local(identity); if (!session) return;
    if (!deps.canWrite() && session.recording) {
      await deps.command('health', {}, session); session = await local(identity, session); deps.changed(); if (!session) return;
    }
    if (!deps.online()) return;
    let status: ServerStatus;
    try { status = (await deps.request<{ journey: ServerStatus }>(identity, path(session.costId, 'status'))).journey; }
    catch (error) {
      const code = (error as { status?: number }).status;
      if ([401, 403, 404].includes(code || 0) && session.recording && deps.owner() === identity) {
        await deps.command('health', {}, session); deps.changed();
      }
      throw error;
    }
    assertOwner(identity);
    if (!status || status.costId !== session.costId || status.sessionId !== session.sessionId) {
      if (session.recording) await deps.command('health', {}, session);
      throw new Error('TRAVEL_SESSION_CONFLICT');
    }
    if (status.stopRequestedAt && !session.recordingStoppedAt && ['TRAVELLING', 'AT_STOP'].includes(session.state)) {
      await deps.command('health', {}, session); session = await local(identity, session); deps.changed(); if (!session) return;
    }
    // Bound each wake-up; retain every unacknowledged suffix for the next one.
    for (let batch = 0; batch < 10; batch++) {
      assertOwner(identity); if (!deps.online()) return;
      if (!await local(identity, session)) return;
      const pending = await deps.pending(session); assertOwner(identity);
      if (!pending.commands.length && !pending.points.length && !pending.evidence.length) return;
      let progressed = false;
      for (const command of pending.commands) {
        assertOwner(identity);
        const rejectionKey = `${identity}:rejected-command:${session.sessionId}`;
        const known = await deps.read<RejectedCommandRecord>(rejectionKey); assertOwner(identity);
        if (known?.operationId === command.body.operationId) return;
        if (command.body.sessionId !== session.sessionId || command.body.deviceId !== session.deviceId) throw new Error('TRAVEL_SESSION_CONFLICT');
        const suffix = command.action === 'arrive' ? 'stops' : command.action === 'depart'
          ? `stops/${encodeURIComponent(String(command.body.stopId))}/depart` : command.action === 'health' || command.action === 'correct' ? 'events' : command.action;
        let result: { journey: { queued: boolean; sessionId?: string; revision?: number } };
        try { result = await deps.request(identity, path(session.costId, suffix), command.body); }
        catch (error) {
          const failure = error as { code?: string; details?: { operationId?: string; sequence?: number; rejectionCode?: string; expectedRevision?: number } };
          const details = failure.details;
          const durable = !!(failure.code === 'TRAVEL_EVENT_REJECTED' && details && details.operationId === command.body.operationId
            && details.sequence === command.body.sequence && details.expectedRevision === command.body.expectedRevision);
          if (durable || rejectedInput(error)) await deps.update<RejectedCommandRecord>(rejectionKey, () => {
            assertOwner(identity); return { operationId: String(command.body.operationId), code: durable ? failure.details!.rejectionCode || failure.code! : failure.code || 'TRAVEL_EVENT_REPAIR_REQUIRED', durable };
          });
          deps.changed(); throw error;
        }
        assertOwner(identity);
        if (result.journey?.queued === false && result.journey.sessionId === session.sessionId
          && Number(result.journey.revision) >= Number(command.body.sequence)) {
          await deps.acknowledge({ operationIds: [String(command.body.operationId)] }, session); progressed = true;
        } else if (result.journey?.queued !== true) throw new Error('TRAVEL_UPLOAD_ACK_REQUIRED');
      }
      // Photos may depend on a just-created stop or reviewed reading correction.
      // Do not send them until every local event has an applied acknowledgement.
      const afterCommands = await local(identity, session); if (!afterCommands) return;
      if (afterCommands.commands.length) { if (progressed) { deps.changed(); continue; } return; }
      if (pending.points.length) {
        const body = { sessionId: session.sessionId, deviceId: session.deviceId, points: pending.points };
        let acknowledgement: { sessionId: string; acknowledgedSequences: number[] };
        if (session.recordingMode === 'native') {
          const credential = await deps.request<{ token: string }>(identity, path(session.costId, 'session-token'), { sessionId: session.sessionId, deviceId: session.deviceId });
          assertOwner(identity); if (!credential.token) throw new Error('TRAVEL_UPLOAD_ACK_REQUIRED');
          acknowledgement = await deps.uploadPoints(path(session.costId, 'session-upload'), credential.token, body);
        } else acknowledgement = await deps.request(identity, path(session.costId, 'location-batches'), body);
        assertOwner(identity);
        if (acknowledgement.sessionId !== session.sessionId || !Array.isArray(acknowledgement.acknowledgedSequences)) throw new Error('TRAVEL_UPLOAD_ACK_REQUIRED');
        const accepted = new Set(acknowledgement.acknowledgedSequences); let through: number | undefined;
        for (const point of pending.points) { if (!accepted.has(point.sequence)) break; through = point.sequence; }
        if (through === undefined) throw new Error('TRAVEL_UPLOAD_ACK_REQUIRED');
        await deps.acknowledge({ throughSequence: through }, session); progressed = true;
      }
      for (const evidence of pending.evidence) {
        assertOwner(identity); if (!evidence.stopId) continue;
        if (!evidence.capturedAt) throw new Error('TRAVEL_EVIDENCE_TIME_REQUIRED');
        const key = `${identity}:rejected-evidence:${session.sessionId}`;
        const rejected = await deps.read<RejectedJourneyEvidence[]>(key); assertOwner(identity);
        if (rejected?.some(item => item.id === evidence.id && item.operationId === evidence.operationId)) continue;
        let response: { evidence: { id: string } };
        try {
          response = await deps.request(identity, path(session.costId, `stops/${encodeURIComponent(evidence.stopId)}/evidence`), {
            operationId: evidence.operationId, role: evidence.role || 'ODOMETER_ARRIVAL', image: evidence.image,
            odometer: evidence.odometer, capturedAt: evidence.capturedAt,
          });
        } catch (error) {
          if (rejectedInput(error)) await deps.update<RejectedJourneyEvidence[]>(key, current => {
            assertOwner(identity); return [...(current || []).filter(item => item.id !== evidence.id), { id: evidence.id, operationId: evidence.operationId,
              stopId: evidence.stopId, role: evidence.role || 'ODOMETER_ARRIVAL', rejectionCode: (error as { code?: string }).code || 'TRAVEL_EVIDENCE_REJECTED' }];
          });
          deps.changed(); throw error;
        }
        assertOwner(identity); if (!response.evidence?.id) throw new Error('TRAVEL_UPLOAD_ACK_REQUIRED');
        await deps.acknowledge({ evidenceIds: [evidence.id] }, session); progressed = true;
      }
      if (progressed) deps.changed(); else return;
    }
  }
  async function repairJourneyCommand(operationId: string, fields: Record<string, unknown>) {
    const identity = owner(); const session = await local(identity); if (!session) throw new Error('NO_JOURNEY');
    const key = `${identity}:rejected-command:${session.sessionId}`;
    const rejected = await deps.read<RejectedCommandRecord>(key); assertOwner(identity);
    if (!rejected || rejected.operationId !== operationId) throw new Error('TRAVEL_EVENT_REPAIR_REQUIRED');
    if (['TRAVEL_CHRONOLOGY', 'TRAVEL_STOP_REQUIRED', 'TRAVEL_SESSION_CONFLICT', 'TRAVEL_REVISION_CONFLICT'].includes(rejected.code)) throw new Error('TRAVEL_EVENT_REPAIR_REQUIRED');
    await deps.replaceCommand(session, operationId, fields, rejected.durable); assertOwner(identity);
    await deps.update<RejectedCommandRecord>(key, current => { assertOwner(identity); return current?.operationId === operationId ? null : current; });
    deps.changed(); void synchronizeJourney().catch(() => deps.changed());
  }
  function finishJourney(extra: Record<string, unknown> = {}) {
    const identity = owner(); const running = finishes.get(identity); if (running) return running;
    const task = finish(identity, extra).finally(() => { if (finishes.get(identity) === task) finishes.delete(identity); });
    finishes.set(identity, task); return task;
  }
  async function finish(identity: string, extra: Record<string, unknown>) {
    const draftKey = `${identity}:finish`;
    let draft = await deps.read<FinishDraft>(draftKey); assertOwner(identity);
    let session = await local(identity);
    if (!session) {
      if (!draft?.completedCost || draft.owner !== identity) throw new Error('TRAVEL_END_REQUIRED');
      await deps.update(draftKey, () => { assertOwner(identity); return null; }); return draft.completedCost;
    }
    if (session.state !== 'ENDING' || session.recording || !session.cutoffAt) throw new Error('TRAVEL_END_REQUIRED');
    if (session.failure) throw new Error(session.failure);
    if (draft && !sameJourney(session, draft)) throw new Error('TRAVEL_SESSION_CONFLICT');
    await synchronizeJourney(); session = await local(identity, session); if (!session) throw new Error('TRAVEL_SESSION_CONFLICT');
    const pending = await deps.pending(session); assertOwner(identity);
    if (pending.commands.length || pending.points.length || session.evidence.some(e => !!e.stopId)) throw new Error('SYNC_PENDING');
    if (!draft) {
      const evidence = pending.evidence.find(e => !e.stopId); if (!evidence) throw new Error('TRAVEL_FINAL_EVIDENCE_REQUIRED');
      const next: FinishDraft = { owner: identity, costId: session.costId, sessionId: session.sessionId, deviceId: session.deviceId,
        payload: { ...extra, workflowVersion: 2, sessionId: session.sessionId, deviceId: session.deviceId,
          endOdometer: evidence.odometer, odometerUnit: evidence.odometerUnit, image: evidence.image,
          completedAt: session.cutoffAt, createTimeEntry: false, idempotencyKey: evidence.operationId } };
      draft = await deps.update<FinishDraft>(draftKey, current => { assertOwner(identity); return current || next; });
    }
    if (!draft || !sameJourney(session, draft)) throw new Error('TRAVEL_SESSION_CONFLICT');
    let cost = draft.completedCost;
    if (!cost) {
      try { cost = (await deps.request<{ cost: CostRecord }>(identity, path(session.costId, 'finish'), draft.payload)).cost; }
      catch (error) {
        // Definitive validation rejection made no commit, so the user can edit
        // readings/FX. Transport failures retain the immutable retry payload.
        if (rejectedInput(error)) await deps.update<FinishDraft>(draftKey, current => {
          assertOwner(identity); return current && current.payload.idempotencyKey === draft!.payload.idempotencyKey && !current.completedCost ? null : current;
        });
        throw error;
      }
    }
    assertOwner(identity); if (cost?.id !== session.costId || !cost.travel?.completedAt) throw new Error('TRAVEL_UPLOAD_ACK_REQUIRED');
    const savedDraft = draft;
    await deps.update<FinishDraft>(draftKey, current => {
      assertOwner(identity); if (!current || current.payload.idempotencyKey !== savedDraft.payload.idempotencyKey) throw new Error('TRAVEL_SESSION_CONFLICT');
      return { ...current, completedCost: cost };
    });
    const latest = await local(identity, session); if (!latest) throw new Error('TRAVEL_SESSION_CONFLICT');
    // Once the frozen final evidence was accepted, additional final captures are
    // redundant local drafts. Stop evidence must never be discarded here.
    await deps.acknowledge({ evidenceIds: latest.evidence.filter(e => !e.stopId).map(e => e.id) }, latest);
    assertOwner(identity); await deps.clear(latest); assertOwner(identity);
    await deps.update(`${identity}:rejected-evidence:${session.sessionId}`, () => null); assertOwner(identity);
    await deps.update(`${identity}:rejected-command:${session.sessionId}`, () => null); assertOwner(identity);
    await deps.update(draftKey, () => { assertOwner(identity); return null; });
    deps.changed(); return cost;
  }
  async function localJourneySnapshot() {
    const identity = owner(); const session = await local(identity);
    if (!session) return { session: null, pendingEvidence: [], pending: false, rejectedCommand: null };
    const pending = await deps.pending(session); const draft = await deps.read<FinishDraft>(`${identity}:finish`);
    const rejected = await deps.read<RejectedJourneyEvidence[]>(`${identity}:rejected-evidence:${session.sessionId}`); assertOwner(identity);
    const failed = await deps.read<RejectedCommandRecord>(`${identity}:rejected-command:${session.sessionId}`); assertOwner(identity);
    const failedCommand = failed && pending.commands.find(command => command.body.operationId === failed.operationId);
    const rejectedCommand: RejectedJourneyCommand | null = failedCommand ? { command: failedCommand, code: failed!.code, durable: failed!.durable,
      repairable: ['arrive', 'depart', 'correct'].includes(failedCommand.action) && !['TRAVEL_CHRONOLOGY', 'TRAVEL_STOP_REQUIRED', 'TRAVEL_SESSION_CONFLICT', 'TRAVEL_REVISION_CONFLICT'].includes(failed!.code) } : null;
    const evidence: JournalEvidence[] = pending.evidence.map(item => ({ ...item, rejectionCode: rejected?.find(row => row.id === item.id && row.operationId === item.operationId)?.rejectionCode }));
    if (draft && sameJourney(session, draft)) {
      // Native recovery may capture a later draft. Show the frozen photo which
      // this uncertain/confirmed request actually uses, until it is settled.
      evidence.splice(0, evidence.length, ...evidence.filter(item => item.stopId));
      evidence.push({ id: String(draft.payload.idempotencyKey),
      operationId: String(draft.payload.idempotencyKey), image: String(draft.payload.image), odometer: Number(draft.payload.endOdometer), odometerUnit: String(draft.payload.odometerUnit) });
    }
    return { session, pendingEvidence: evidence, pending: !!(pending.commands.length || pending.points.length || session.evidence.length || draft), rejectedCommand };
  }
  async function recoverJourneyOwner() {
    const identity = owner(); const current = await local(identity); if (current) return current;
    const response = await deps.request<{ travel: CostRecord | null }>(identity, '/api/costs/travel/active'); assertOwner(identity); return response.travel;
  }
  return { readJourneyDraft, startJourney, journeyAction, repairJourneyCommand, synchronizeJourney, finishJourney, localJourneySnapshot, recoverJourneyOwner };
}
